import type { HttpErrorResponse } from '@angular/common/http';

import type { EntityState, PickActions } from '@recruitee/ngrx';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@recruitee/ngrx';
import { stableStringify } from '@recruitee/utils';
import type {
  DataSourceId,
  FetchRecordsPayload,
  GroupParams,
  LayoutRecordsCountPayload,
  AnyGroup,
  LayoutRecordsParams,
  RecordsState,
} from '@recruitee/views-types';

export interface RecordsQueryParams {
  limit: number;
  page: number;
}

export interface RecordsFetchStoreOptions {
  concat?: boolean;
  customId?: string;
}

export const getRecordsId = (
  dataSourceId: string,
  groupParams?: GroupParams | null,
  swimlaneParams?: LayoutRecordsParams | null,
) => {
  const swimlane = swimlaneParams?.type === 'board' ? swimlaneParams.swimlane : null;
  return [
    dataSourceId,
    groupParams?.configId,
    stableStringify(groupParams?.value),
    swimlane?.configId,
    stableStringify(swimlane?.value),
  ].join(':');
};
const splitRecordsId = (recordsId: string) =>
  <
    [
      dataSource: string,
      groupingId: string,
      groupId: string,
      swimlaneGroupingId: string,
      swimlaneId: string,
    ]
  >recordsId.split(':');
const idSelector: any = (entity: { id: number }) => entity.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<RecordsState>()(
  'ViewRecords',
  {
    idSelector,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      const fetch = (
        state: EntityState<RecordsState>,
        payload: {
          recordsId: string;
          dataSourceId: string;
          data: FetchRecordsPayload;
          queryParams?: RecordsQueryParams;
          storeOptions?: RecordsFetchStoreOptions;
        },
      ) =>
        adapter.markAsPending(state, {
          scope: null,
          entities: [payload.recordsId],
        });

      return {
        fetch,
        fetchSuccess: (state, payload: { data: RecordsState; concat: boolean }) => {
          const currentData = state.entityMap[payload.data.id];
          const data = payload.concat
            ? {
                ...payload.data,
                records: [...(currentData?.records ?? []), ...payload.data.records],
              }
            : {
                ...payload.data,
              };
          return defaultActions.fetchOneSuccess(state, { data });
        },
        fetchFailed: (state, payload: { error: HttpErrorResponse; id: string }) =>
          defaultActions.fetchOneFailed(state, payload),

        fetchAll: (
          state: EntityState<RecordsState>,
          payload: {
            recordsId: string;
            dataSourceId: DataSourceId;
            data: FetchRecordsPayload;
            uuid: string;
          },
        ) => state,
        fetchAllSuccess: (state, payload: { data: RecordsState; uuid: string }) =>
          defaultActions.fetchOneSuccess(state, payload),
        fetchAllFailed: (state, payload: { id: string; uuid: string; error: HttpErrorResponse }) =>
          defaultActions.fetchOneFailed(state, payload),

        refresh: (
          state,
          payload: {
            recordsId: string;
            dataSourceId: string;
            meta: RecordsState['meta'];
            data: FetchRecordsPayload;
          },
        ) => state,
        refreshSuccess: (state, payload: { data: RecordsState }) =>
          defaultActions.fetchOneSuccess(state, payload),
        refreshFailed: (
          state,
          payload: { recordsId: string; dataSourceId: string; data: FetchRecordsPayload },
        ) => state,

        fetchViewAllRecordsCount: (
          state,
          payload: { dataSourceId: string; recordsId: string; data: LayoutRecordsCountPayload },
        ) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.recordsId),
            next =>
              adapter.markAsPending(next, {
                scope: null,
                entities: [payload.recordsId],
              }),
          ),
        fetchViewAllRecordsCountSuccess: defaultActions.fetchOneSuccess,
        fetchViewAllRecordsCountFailed: defaultActions.fetchOneFailed,

        clearById: (state, payload: { recordsId: string }) =>
          adapter.removeOne(state, payload.recordsId),
        clear: (state, payload: { dataSourceId: string; group?: AnyGroup | null }) => {
          const recordsIds = Object.keys(state.entityMetaMap).filter(id => {
            const [dataSourceId, _, groupId] = splitRecordsId(id);
            if (payload.group === undefined) return dataSourceId === payload.dataSourceId;

            return (
              dataSourceId === payload.dataSourceId &&
              groupId === stableStringify(payload.group?.id)
            );
          });
          return adapter.removeMany(state, recordsIds);
        },
      };
    },
  },
);
export const ViewRecordsActions: PickActions<
  typeof actions,
  'fetch' | 'fetchAll' | 'fetchViewAllRecordsCount' | 'refresh'
> &
  Pick<typeof actions, 'clear' | 'clearById'> = actions;
export {
  reducer as ViewRecordsReducer,
  namespace as ViewRecordsNamespace,
  selectors as ViewRecordsSelectors,
};
